import React, { FunctionComponent, useState, useEffect } from 'react';

import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import 'social/publisher/source-customizer/SourceCustomizer.scss';
import { PublisherError, SelectedSource, SourceName, SourceReadableNameMap, SourceStates } from '../Publisher.types';
import { usePublisherDispatch } from '../PublisherContext';

interface Props {
    selectedSources: SelectedSource;
    customizeSourceKey: SourceName | 'ALL_SOURCES';
    isCustomizingBySource: boolean;
    responseEditorRef: any;
    sourceStates: SourceStates;
    publisherErrors: PublisherError[];
    promisesInTransit: string[];
}

export const SourceCustomizer: FunctionComponent<Props> = ({
    selectedSources,
    customizeSourceKey,
    isCustomizingBySource,
    responseEditorRef,
    sourceStates,
    publisherErrors,
    promisesInTransit,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    const [selectedSourceKeys, setSelectedSourceKeys] = useState<SourceName[]>([]);
    const [previousCustomizeSourceKey, setPreviousCustomizeSourceKey] = useState<SourceName | 'ALL_SOURCES'>(
        'ALL_SOURCES',
    );

    useEffect(() => {
        const sourceKeys: SourceName[] = [];

        Object.keys(selectedSources).forEach((key) => {
            if (selectedSources[key]) {
                sourceKeys.push(key as SourceName);
            }
        });
        setSelectedSourceKeys(sourceKeys);

        if (sourceKeys.length === 0) {
            dispatch({ type: 'set-is-customizing-by-source', payload: { value: false } });
            setPreviousCustomizeSourceKey(customizeSourceKey);
            dispatch({ type: 'set-customize-source', payload: { value: 'ALL_SOURCES' } });
        } else if (!selectedSources[customizeSourceKey] && customizeSourceKey !== 'ALL_SOURCES') {
            setPreviousCustomizeSourceKey(customizeSourceKey);
            dispatch({ type: 'set-customize-source', payload: { value: sourceKeys[0] } });
        }
    }, [selectedSources, dispatch, customizeSourceKey]);

    useEffect(() => {
        // don't change contents if customizeSourceKey hasn't changed
        if (responseEditorRef?.current?.setContents && previousCustomizeSourceKey !== customizeSourceKey) {
            if (sourceStates[customizeSourceKey]) {
                responseEditorRef.current.setContents(sourceStates[customizeSourceKey]?.content);
                responseEditorRef.current.setMedia(sourceStates[customizeSourceKey]?.media);
            } else {
                responseEditorRef.current.setContents(sourceStates?.ALL_SOURCES?.content);
                responseEditorRef.current.setMedia(sourceStates?.ALL_SOURCES?.media);
            }
        }
    }, [customizeSourceKey, responseEditorRef, sourceStates, previousCustomizeSourceKey]);

    const handleChangeTab = (sourceName: SourceName | 'ALL_SOURCES') => {
        setPreviousCustomizeSourceKey(customizeSourceKey);
        dispatch({ type: 'set-customize-source', payload: { value: sourceName } });
    };

    const tabHasError = (sourceName: SourceName): boolean => {
        let hasError = false;

        publisherErrors.forEach((error) => {
            if (error.source === sourceName) {
                hasError = true;
            }
        });

        return hasError;
    };

    return (
        <div
            className={`source-customizer-container ${
                (promisesInTransit.includes('MEDIA_UPLOAD') || promisesInTransit.includes('MEDIA_VALIDATION')) &&
                'disabled'
            }`}
        >
            {!isCustomizingBySource ? (
                <>
                    <div className="source-customizer-tab active">{t('ALL_SOURCES')}</div>
                    {selectedSourceKeys.length > 1 && (
                        <div className="source-customizer-button">
                            <OverlayTrigger
                                key="CUSTOMIZE_POST_BY_SOURCE"
                                placement="top"
                                overlay={
                                    <Tooltip id="customize-post-by-source-tooltip">
                                        {t('CANT_EDIT_ORIGINAL_DURING_CUSTOMIZE_BY_SOURCE')}
                                    </Tooltip>
                                }
                            >
                                <Button
                                    variant="secondary"
                                    onClick={(): void => {
                                        dispatch({ type: 'set-is-customizing-by-source', payload: { value: true } });
                                        setPreviousCustomizeSourceKey(customizeSourceKey);
                                        dispatch({
                                            type: 'set-customize-source',
                                            payload: { value: selectedSourceKeys[0] },
                                        });
                                    }}
                                >
                                    <span>{t('CUSTOMIZE_POST_BY_SOURCE')}</span>
                                </Button>
                            </OverlayTrigger>
                        </div>
                    )}
                </>
            ) : (
                <>
                    {selectedSourceKeys.map((key, index) => {
                        return (
                            <div
                                key={key}
                                role="button"
                                onClick={() => handleChangeTab(key as SourceName)}
                                onKeyDown={() => handleChangeTab(key as SourceName)}
                                tabIndex={index + 1}
                                className={`source-customizer-tab button ${
                                    customizeSourceKey === key ? 'active' : ''
                                } ${tabHasError(key as SourceName) ? 'errored' : ''}`}
                            >
                                <i className={`social-icon ${key.toLowerCase()}`} />
                                {SourceReadableNameMap[key]}
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};
